import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Product, Category, Transaction, DashboardSummary, Location, ProductLocation } from '../types';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface InventoryContextType {
  products: Product[];
  categories: Category[];
  transactions: Transaction[];
  locations: Location[];
  productLocations: ProductLocation[];
  addProduct: (product: Omit<Product, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<Product | void>;
  updateProduct: (product: Product) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  addCategory: (category: Omit<Category, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<Category>;
  updateCategory: (category: Category) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  addTransaction: (transaction: Omit<Transaction, 'id' | 'user_id' | 'date'>) => Promise<void>;
  getDashboardSummary: () => DashboardSummary;
  getProductById: (id: string) => Product | undefined;
  getCategoryById: (id: string) => Category | undefined;
  getCategoryColor: (categoryId: string) => string;
  getCategoryName: (categoryId: string) => string;
  isLowStock: (product: Product) => boolean;
  getProductTotalStock: (productId: string) => number;
  getProductLocations: (productId: string) => ProductLocation[];
  importData: (data: { products: any[]; categories: any[]; transactions: any[] }) => Promise<void>;
}

const InventoryContext = createContext<InventoryContextType | undefined>(undefined);

export const useInventory = () => {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error('useInventory must be used within an InventoryProvider');
  }
  return context;
};

interface InventoryProviderProps {
  children: ReactNode;
}

export const InventoryProvider: React.FC<InventoryProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [productLocations, setProductLocations] = useState<ProductLocation[]>([]);

  // Load initial data
  useEffect(() => {
    if (user) {
      loadData();
    } else {
      // Clear data when user logs out
      setProducts([]);
      setCategories([]);
      setTransactions([]);
      setLocations([]);
      setProductLocations([]);
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    try {
      console.log('📊 Loading inventory data...');
      
      // Load categories first
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .order('name');
      if (categoriesError) throw categoriesError;
      if (categoriesData) setCategories(categoriesData);
      console.log('✅ Categories loaded:', categoriesData?.length || 0);

      // Load products (without nested category selection)
      const { data: productsData, error: productsError } = await supabase
        .from('supplies')
        .select('*')
        .order('name');
      if (productsError) throw productsError;
      if (productsData) setProducts(productsData);
      console.log('✅ Products loaded:', productsData?.length || 0);

      // Load transactions
      const { data: transactionsData, error: transactionsError } = await supabase
        .from('transactions')
        .select('*')
        .order('date', { ascending: false });
      if (transactionsError) throw transactionsError;
      if (transactionsData) setTransactions(transactionsData);
      console.log('✅ Transactions loaded:', transactionsData?.length || 0);

      // Load locations
      const { data: locationsData, error: locationsError } = await supabase
        .from('locations')
        .select('*')
        .order('is_default', { ascending: false })
        .order('name');
      if (locationsError) throw locationsError;
      if (locationsData) setLocations(locationsData);
      console.log('✅ Locations loaded:', locationsData?.length || 0);

      // Load product locations
      const { data: productLocationsData, error: productLocationsError } = await supabase
        .from('product_locations')
        .select(`
          *,
          location:locations(*)
        `)
        .order('quantity', { ascending: false });
      if (productLocationsError) throw productLocationsError;
      if (productLocationsData) setProductLocations(productLocationsData);
      console.log('✅ Product locations loaded:', productLocationsData?.length || 0);
      
      console.log('🎉 All inventory data loaded successfully');
    } catch (error) {
      console.error('❌ Error loading inventory data:', error);
      
      // Provide specific error guidance
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        console.error('🌐 Network connectivity issue detected:');
        console.error('  • Check your internet connection');
        console.error('  • Verify Supabase environment variables are set correctly');
        console.error('  • Ensure your Supabase project is active');
        console.error('  • Check browser console for CORS or network errors');
        
        // Show user-friendly error message
        alert('Unable to connect to the database. Please check your internet connection and refresh the page.');
      } else {
        console.error('🔧 Database error details:', {
          message: error?.message,
          code: error?.code,
          details: error?.details,
          hint: error?.hint
        });
      }
    }
  };

  const addProduct = async (product: Omit<Product, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) return;

    // Ensure category_id, expiration_date, and lot_number are null if they're empty strings or undefined
    const productData = {
      ...product,
      category_id: product.category_id === '' || product.category_id === undefined ? null : product.category_id,
      expiration_date: !product.expiration_date || product.expiration_date === '' ? null : product.expiration_date,
      lot_number: !product.lot_number || product.lot_number === '' ? null : product.lot_number,
      user_id: user.id
    };

    const { data, error } = await supabase
      .from('supplies')
      .insert([productData])
      .select()
      .single();

    if (error) throw error;
    if (data) {
      setProducts(prev => [...prev, data]);
      return data;
    }
  };

  const updateProduct = async (product: Product) => {
    if (!user) return;

    // Ensure category_id, expiration_date, and lot_number are null if they're empty strings or undefined
    const productData = {
      ...product,
      category_id: product.category_id === '' || product.category_id === undefined ? null : product.category_id,
      expiration_date: !product.expiration_date || product.expiration_date === '' ? null : product.expiration_date,
      lot_number: !product.lot_number || product.lot_number === '' ? null : product.lot_number
    };

    const { data, error } = await supabase
      .from('supplies')
      .update(productData)
      .eq('id', product.id)
      .select()
      .single();

    if (error) throw error;
    if (data) {
      setProducts(prev =>
        prev.map(p => (p.id === data.id ? data : p))
      );
    }
  };

  const deleteProduct = async (id: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('supplies')
      .delete()
      .eq('id', id);

    if (error) throw error;
    setProducts(prev => prev.filter(p => p.id !== id));
  };

  const addCategory = async (category: Omit<Category, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) throw new Error('No user authenticated');

    const { data, error } = await supabase
      .from('categories')
      .insert([{ ...category, user_id: user.id }])
      .select()
      .single();

    if (error) throw error;
    if (data) {
      setCategories(prev => [...prev, data]);
      return data;
    }
    throw new Error('Failed to create category');
  };

  const updateCategory = async (category: Category) => {
    if (!user) return;

    const { data, error } = await supabase
      .from('categories')
      .update(category)
      .eq('id', category.id)
      .select()
      .single();

    if (error) throw error;
    if (data) {
      setCategories(prev =>
        prev.map(c => (c.id === data.id ? data : c))
      );
    }
  };

  const deleteCategory = async (id: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', id);

    if (error) throw error;
    setCategories(prev => prev.filter(c => c.id !== id));
    
    // Update products that used this category
    setProducts(prev =>
      prev.map(p => (p.category_id === id ? { ...p, category_id: null } : p))
    );
  };

  const addTransaction = async (transaction: Omit<Transaction, 'id' | 'user_id' | 'date'>) => {
    if (!user) return;

    try {
      // Get the current product state
      const { data: product, error: productError } = await supabase
        .from('products')
        .select('*')
        .eq('id', transaction.product_id)
        .single();

      if (productError) throw productError;
      if (!product) throw new Error('Product not found');

      // Calculate new stock quantity
      let newStockQuantity = product.stock_quantity;
      if (transaction.type === 'in') {
        newStockQuantity += transaction.quantity;
      } else if (transaction.type === 'out') {
        newStockQuantity -= transaction.quantity;
      } else if (transaction.type === 'adjustment') {
        newStockQuantity += transaction.quantity; // Can be positive or negative
      }

      // Update product stock
      const { error: updateError } = await supabase
        .from('products')
        .update({ stock_quantity: newStockQuantity })
        .eq('id', product.id);

      if (updateError) throw updateError;

      // Create transaction record
      const { data: newTransaction, error: transactionError } = await supabase
        .from('transactions')
        .insert([{
          ...transaction,
          user_id: user.id,
          user_reference: user.email?.split('@')[0] || 'unknown',
        }])
        .select()
        .single();

      if (transactionError) throw transactionError;

      // Update local state
      setProducts(prev =>
        prev.map(p => p.id === product.id
          ? { ...p, stock_quantity: newStockQuantity }
          : p
        )
      );
      
      if (newTransaction) {
        setTransactions(prev => [newTransaction, ...prev]);
      }

      // Reload product locations to ensure consistency
      await loadData();

    } catch (error) {
      console.error('Error in transaction:', error);
      throw error;
    }
  };

  const importData = async (data: { products: any[]; categories: any[]; transactions: any[] }) => {
    if (!user) return;

    try {
      // Import categories first and maintain a mapping of names to IDs
      const categoryNameToId = new Map<string, string>();
      
      for (const category of data.categories) {
        const { name, description, color } = category;
        
        // Check if category already exists
        const { data: existingCategory } = await supabase
          .from('categories')
          .select('id, name')
          .eq('name', name)
          .eq('user_id', user.id)
          .single();

        if (existingCategory) {
          categoryNameToId.set(name, existingCategory.id);
        } else {
          const newCategory = await addCategory({ name, description, color });
          if (newCategory) {
            categoryNameToId.set(name, newCategory.id);
          }
        }
      }

      // Import products with category ID lookup
      for (const product of data.products) {
        const {
          name,
          sku,
          description,
          category_name,
          price,
          cost_price,
          stock_quantity,
          min_stock_level,
          image,
          location
        } = product;
        
        // Look up category ID from name
        const category_id = category_name ? categoryNameToId.get(category_name) : null;
        
        await addProduct({
          name,
          sku,
          description,
          category_id,
          price: Number(price),
          cost_price: Number(cost_price),
          stock_quantity: Number(stock_quantity),
          min_stock_level: Number(min_stock_level),
          image,
          location
        });
      }

      // Import transactions
      for (const transaction of data.transactions) {
        const {
          product_id,
          type,
          quantity,
          notes,
          status,
          currency
        } = transaction;

        await addTransaction({
          product_id,
          type,
          quantity: Number(quantity),
          notes,
          status,
          currency
        });
      }

      // Reload data to ensure everything is in sync
      await loadData();
    } catch (error) {
      console.error('Error importing data:', error);
      throw error;
    }
  };

  const getProductById = (id: string) => {
    return products.find(product => product.id === id);
  };

  const getCategoryById = (id: string) => {
    return categories.find(category => category.id === id);
  };

  const getCategoryColor = (categoryId: string | null) => {
    if (!categoryId) return '#CBD5E1'; // Default color for no category
    const category = categories.find(cat => cat.id === categoryId);
    return category?.color || '#CBD5E1'; // Default color
  };

  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return 'Uncategorized';
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || 'Uncategorized';
  };

  const getProductTotalStock = (productId: string) => {
    const locationStock = productLocations
      .filter(pl => pl.product_id === productId)
      .reduce((total, pl) => total + pl.quantity, 0);

    // If there are location-based stock records, use those
    if (locationStock > 0) {
      return locationStock;
    }

    // Otherwise, fall back to the product's stock_quantity field
    const product = products.find(p => p.id === productId);
    return product?.stock_quantity || 0;
  };

  const getProductLocations = (productId: string) => {
    return productLocations.filter(pl => pl.product_id === productId);
  };

  const isLowStock = (product: Product) => {
    const locationStock = getProductTotalStock(product.id);
    const actualStock = Math.max(locationStock, product.stock_quantity);
    return actualStock <= product.min_stock_level;
  };

  const hasStockMismatch = (product: Product) => {
    const locationStock = productLocations
      .filter(pl => pl.product_id === product.id)
      .reduce((total, pl) => total + pl.quantity, 0);

    // Mismatch if: stock exists but no location records, OR location totals don't match product stock
    return (
      (product.stock_quantity > 0 && locationStock === 0) ||
      (locationStock > 0 && product.stock_quantity !== locationStock)
    );
  };

  const getDashboardSummary = (): DashboardSummary => {
    const totalProducts = products.length;
    const totalStock = products.reduce((sum, product) => sum + getProductTotalStock(product.id), 0);
    const lowStockCount = products.filter(product => isLowStock(product)).length;

    const categoryBreakdown = categories.map(category => {
      const count = products.filter(product => product.category_id === category.id).length;
      return {
        categoryId: category.id,
        categoryName: category.name,
        count,
      };
    });

    return {
      totalProducts,
      totalStock,
      lowStockCount,
      categoryBreakdown,
      recentTransactions: transactions.slice(0, 5),
    };
  };

  const value = {
    products,
    categories,
    transactions,
    locations,
    productLocations,
    addProduct,
    updateProduct,
    deleteProduct,
    addCategory,
    updateCategory,
    deleteCategory,
    addTransaction,
    getDashboardSummary,
    getProductById,
    getCategoryById,
    getCategoryColor,
    getCategoryName,
    isLowStock,
    getProductTotalStock,
    getProductLocations,
    hasStockMismatch,
    importData,
  };

  return <InventoryContext.Provider value={value}>{children}</InventoryContext.Provider>;
};